import { useQuery } from "@apollo/client";
import moment from "moment";
import { ReactNode, useState } from "react";
import {
  GetComplianceProgramTimelineQuery,
  GetComplianceProgramTimelineQueryVariables,
  TimelineEntryKind,
} from "types/graphql-schema";

import useLabel, { UseLabelType } from "@apps/use-label/use-label";
import Loading from "@components/loading/loading";
import { classNames } from "@helpers/css";

import getComplianceProgramTimelineQuery from "../graphql/get-compliance-program-timeline-query";
import { useProgramsSection } from "../programs-section-context";
import {
  TimelineRow,
  channelLabels,
  conditionalTimelineMessage,
  opensAfter,
  sendingKinds,
  skipReasonLabels,
  stepOnly,
} from "./compliance-program-timeline-vocabulary";

/**
 * How many people a row reaches, in words. Only a sending row has an audience, and
 * a row whose recipients nobody can know yet says so rather than reporting nobody.
 */
const audience = (row: TimelineRow) => {
  if (!sendingKinds.includes(row.kind)) {
    return null;
  }
  if (!row.recipientsKnown) {
    return "recipients not known yet";
  }
  return `${row.recipientCount} ${
    row.recipientCount === 1 ? "person" : "people"
  }`;
};

/** One reason when every skipped person shares it, which is the usual case. */
const sharedSkipReason = (row: TimelineRow) => {
  const reasons = new Set(row.skipped.map((skip) => skip.reason));
  return reasons.size === 1
    ? skipReasonLabels[row.skipped[0].reason]
    : "several reasons";
};

const TimelineItem = ({ row }: { row: TimelineRow }) => {
  const [expanded, setExpanded] = useState(false);
  const sends = sendingKinds.includes(row.kind);
  const details = [
    audience(row),
    row.note,
    row.channels.map((channel) => channelLabels[channel] ?? channel).join(", "),
  ].filter(Boolean);

  return (
    // A row whose day has passed dims as a whole. Dimming only its label left the
    // badge, the counts and the skipped control at full strength, so half the row
    // still read as something about to happen.
    <div
      className={classNames(
        "flex flex-col gap-0.5 border-l-2 border-blue-300 pl-2.5",
        row.isPast && "opacity-60",
      )}
    >
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        {sends && (
          <span className="shrink-0 border border-blue-500 px-1.5 text-[10px] font-medium uppercase tracking-wide text-blue-600">
            {row.kind === TimelineEntryKind.Reminder ? "Reminder" : "Sends"}
          </span>
        )}
        <span className="text-sm">{row.label}</span>
        {details.length > 0 && (
          <span className="text-xs text-gray-500">{details.join(" · ")}</span>
        )}
        {sends && row.skippedCount > 0 && (
          <button
            type="button"
            className="shrink-0 bg-amber-50 px-1.5 text-xs text-amber-800 underline"
            aria-expanded={expanded}
            onClick={() => setExpanded(!expanded)}
          >
            {row.skippedCount} skipped · {sharedSkipReason(row)}
          </button>
        )}
      </div>
      {expanded && (
        <ul className="pl-1 text-xs text-gray-500">
          {row.skipped.map((skip, index) => (
            <li key={`${skip.user?.id ?? index}-${skip.reason}`}>
              {skip.user?.name ?? "Somebody who has left"} —{" "}
              {skipReasonLabels[skip.reason] ?? skip.reason}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/**
 * One heading inside a day: Due, Opens, Opens next, Messages.
 *
 * A single date can carry a dozen rows -- every step falling due, every step
 * opening, and three package messages all land on the delivery date -- and drawn
 * as one flat list they read as a wall. Named groups turn that day into three
 * short answers to three different questions.
 */
const TimelineSection = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <div>
    <div className="mb-1.5 text-[10px] font-bold uppercase tracking-[0.08em] text-gray-400">
      {label}
    </div>
    {children}
  </div>
);

/**
 * One item to a line, each with a marker saying where it starts.
 *
 * These were columns once, to keep a dense day short. Two names sharing a line is
 * two names only if you already know that: "Self review     Peer review" reads as
 * one thing with a wide gap in it just as readily, and a row of three reads as a
 * sentence. Down the page, one marker each, there is nothing to work out.
 *
 * The marker says which kind of item it is. A bullet is one of several things
 * happening; an arrow is a step waiting on the one it names.
 */
const TimelineMarkedList = ({
  rows,
  marker,
  text,
}: {
  rows: TimelineRow[];
  marker: "bullet" | "arrow";
  text: (row: TimelineRow) => string;
}) => (
  <div className="flex flex-col gap-1 text-sm text-gray-600">
    {rows.map((row, index) => (
      <div
        key={`${row.kind}-${row.label}-${index}`}
        className={classNames("flex gap-1.5", row.isPast && "opacity-60")}
      >
        {/* A real element, not a `before:content-[…]` pseudo-element. Tailwind
            generates that class from the source text, where the escape is written
            `\\2022`, while React hands the DOM the string that escape evaluates
            to — so the selector never matched and no marker was drawn at all.
            A character in the markup cannot come apart that way.

            It starts the line rather than being indented into one, so the markers
            line up with the heading above them. */}
        <span aria-hidden="true" className="select-none text-gray-400">
          {marker === "arrow" ? "\u2192" : "\u2022"}
        </span>
        {/* Its own box, so a name that wraps stays under itself rather than
            running back under the marker. */}
        <span className="min-w-0">{text(row)}</span>
      </div>
    ))}
  </div>
);

/** The rows that land on one day, in the order the plan gives them. */
type TimelineDay = { date: string; rows: TimelineRow[] };

const groupByDate = (rows: readonly TimelineRow[]): TimelineDay[] => {
  const days: TimelineDay[] = [];
  rows.forEach((row) => {
    const current = days[days.length - 1];
    if (current && current.date === row.date) {
      current.rows.push(row);
    } else {
      days.push({ date: row.date, rows: [row] });
    }
  });
  return days;
};

/**
 * What the day is about, in a few words. Kickoff and a step falling due are the
 * two things an admin looks for in a calendar, so they name the day when present.
 */
const dayHeadline = (day: TimelineDay) => {
  if (day.rows.some((row) => row.kind === TimelineEntryKind.Kickoff)) {
    return "Kickoff";
  }
  const due = day.rows.find((row) => row.kind === TimelineEntryKind.StepDue);
  return due ? due.label : day.rows[0].label;
};

/** The kinds the named groups below take. Every other kind falls through. */
const groupedKinds: TimelineEntryKind[] = [
  TimelineEntryKind.StepDue,
  TimelineEntryKind.Kickoff,
  TimelineEntryKind.StepOpens,
  ...sendingKinds,
];

/**
 * The day, split into the questions its rows answer.
 *
 * `opens` and `opensNext` are the same kind of row told apart by whether it waits
 * on another step: one is a date, the other is a dependency, and mixing them was
 * half of what made a busy day unreadable.
 *
 * `rest` is every row the named groups do not take, and it is not a formality: an
 * ongoing review's whole plan is `ongoing_entry` rows, and the one line saying a
 * recurring review makes its next copy lands on the due date, where the headline
 * is already taken by a step. Without it those days draw an empty box.
 */
const sectionsOf = (day: TimelineDay, label: UseLabelType) => {
  const opening = day.rows.filter(
    (row) =>
      row.kind === TimelineEntryKind.Kickoff ||
      row.kind === TimelineEntryKind.StepOpens,
  );
  return {
    due: day.rows.filter((row) => row.kind === TimelineEntryKind.StepDue),
    opens: opening.filter((row) => !opensAfter(row, label)),
    opensNext: opening.filter((row) => opensAfter(row, label)),
    messages: day.rows.filter((row) => sendingKinds.includes(row.kind)),
    rest: day.rows.filter((row) => !groupedKinds.includes(row.kind)),
  };
};

/** The four named groups, in the order the day happens in, then the rest. */
const TimelineDayBody = ({ day }: { day: TimelineDay }) => {
  const label = useLabel();
  const { due, opens, opensNext, messages, rest } = sectionsOf(day, label);

  return (
    <div className="flex flex-1 flex-col gap-3.5 px-4 py-3.5">
      {due.length > 0 && (
        <TimelineSection label="Due">
          <TimelineMarkedList
            rows={due}
            marker="bullet"
            text={(row) => stepOnly(row, label)}
          />
        </TimelineSection>
      )}
      {opens.length > 0 && (
        <TimelineSection label="Opens">
          <TimelineMarkedList
            rows={opens}
            marker="bullet"
            text={(row) => stepOnly(row, label)}
          />
        </TimelineSection>
      )}
      {opensNext.length > 0 && (
        <TimelineSection label="Opens next">
          <TimelineMarkedList
            rows={opensNext}
            marker="arrow"
            text={(row) => opensAfter(row, label) ?? row.label}
          />
        </TimelineSection>
      )}
      {messages.length > 0 && (
        <TimelineSection label="Messages">
          <div className="flex flex-col gap-3">
            {messages.map((row, index) => (
              <TimelineItem
                key={`${row.kind}-${row.label}-${index}`}
                row={row}
              />
            ))}
          </div>
        </TimelineSection>
      )}
      {/* Under no heading of their own: there is no question these rows answer
          together, and on an ongoing review they are the whole day, where a word
          above every line would only be that word repeated. */}
      {rest.length > 0 && (
        <div className="flex flex-col gap-3">
          {rest.map((row, index) => (
            <TimelineItem key={`${row.kind}-${row.label}-${index}`} row={row} />
          ))}
        </div>
      )}
    </div>
  );
};

const ComplianceProgramTimelineDay = ({
  day,
  headline,
}: {
  day: TimelineDay;
  headline: boolean;
}) => (
  // Side by side when the card is wide enough for a date column, stacked into a
  // date header when it is not. A container query rather than a screen one: the
  // card is the full width of the review page but a narrow column beside the setup
  // form, and a viewport breakpoint cannot tell those apart.
  <div className="flex flex-col border-b border-gray-200 last:border-b-0 @[28rem]/timeline:flex-row">
    <div className="shrink-0 border-b border-gray-200 bg-gray-50 px-4 py-3 text-sm font-medium @[28rem]/timeline:w-44 @[28rem]/timeline:border-b-0 @[28rem]/timeline:border-r @[28rem]/timeline:py-4">
      {moment(day.date).format("MMM D, YYYY")}
      {headline && (
        // Beside the date in the stacked header bar, under it in the date
        // column, where there is room for two lines and none to spare across.
        <span className="ml-2 inline text-[11px] font-normal uppercase tracking-wide text-gray-500 @[28rem]/timeline:ml-0 @[28rem]/timeline:mt-0.5 @[28rem]/timeline:block">
          {dayHeadline(day)}
        </span>
      )}
    </div>
    <TimelineDayBody day={day} />
  </div>
);

/**
 * The plan itself: one block per day, in a single bordered card.
 *
 * Takes rows rather than fetching them, so the review page and the setup form's
 * panel render the same plan the same way. `empty` is what the card says when
 * there are no rows, which is never the same sentence twice: a page that failed to
 * load, a form with no dates yet and a review that really sends nothing are three
 * different answers and only the caller knows which one it has.
 *
 * `headline` names each day after the thing it is mostly about. A list already
 * narrowed to one kind of row turns it off, because then the headline is only ever
 * the first row's own label said twice.
 */
export const ComplianceProgramTimelineDays = ({
  rows,
  empty,
  headline = true,
}: {
  rows: readonly TimelineRow[];
  empty: ReactNode;
  headline?: boolean;
}) => {
  // Not memoised: every caller builds its rows array in the render that uses it,
  // so a memo on that identity would never hit.
  const days = groupByDate(rows);

  return (
    <div className="@container/timeline overflow-hidden rounded-sm border border-gray-200 bg-white">
      {days.map((day) => (
        <ComplianceProgramTimelineDay
          key={day.date}
          day={day}
          headline={headline}
        />
      ))}
      {days.length === 0 && (
        <div className="p-4 text-center text-sm text-gray-500">{empty}</div>
      )}
    </div>
  );
};

/**
 * The Timeline tab of a review page.
 *
 * `ongoing` and `kickoffAfterDays` come from the page's own query rather than from
 * this one, because an ongoing review has no dates of its own and the backend dates
 * its plan from today. Without them the rows below would read as days the admin
 * chose, which they are not.
 */
const ComplianceProgramTimeline = ({
  complianceProgramId,
  ongoing,
  kickoffAfterDays,
}: {
  complianceProgramId: number;
  ongoing: boolean;
  kickoffAfterDays: number;
}) => {
  const { noun } = useProgramsSection();
  const { data, loading, error } = useQuery<
    GetComplianceProgramTimelineQuery,
    GetComplianceProgramTimelineQueryVariables
  >(getComplianceProgramTimelineQuery, {
    fetchPolicy: "cache-and-network",
    variables: { complianceProgramId },
  });

  const rows = data?.complianceProgram?.timeline ?? [];

  return (
    <div className="flex flex-col gap-4" aria-label={`${noun} timeline`}>
      <div className="text-sm text-gray-500">
        What this {noun.toLowerCase()} will do, and who each message reaches.
      </div>

      {ongoing && (
        <div className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">
          {conditionalTimelineMessage(noun.toLowerCase(), kickoffAfterDays).map(
            (sentence) => (
              <p key={sentence} className="mt-1 first:mt-0">
                {sentence}
              </p>
            ),
          )}
        </div>
      )}

      <ComplianceProgramTimelineDays
        rows={rows}
        empty={
          loading ? (
            <Loading size="5" />
          ) : /* A failed request must not read as "this does nothing". */
          error ? (
            "This timeline could not be loaded. Reload the page to try again."
          ) : (
            "Nothing is scheduled yet. Set a start date and a due date to see the timeline."
          )
        }
      />
    </div>
  );
};

export default ComplianceProgramTimeline;
